import {
    Component,
    Host,
    h,
    Event,
    EventEmitter,
    Prop,
    State,
    Watch,
} from "@stencil/core";

/**
 * @slot (default) - The sidebar content
 */
@Component({
    tag: "uic-sidebar",
    styleUrl: "sidebar.scss",
    shadow: false,
})
export class Sidebar {
    /** Unique name used to identify the sidebar */
    @Prop() name: string;

    /** On which side should the sidebar be positioned */
    @Prop() side: "left" | "right" = "left";

    /** Flag that indicates if the sidebar top should be aligned with a header */
    @Prop() alignHeader?: boolean = false;

    /** Flag that indicates if the sidebar bottom should be aligned with a footer */
    @Prop() alignFooter?: boolean = false;

    /** Flag that indicates if the sidebar is opened or not */
    @Prop() opened?: boolean = false;

    /** Transition status */
    @State() status: "closed" | "closing" | "opening" | "opened" = "closed";

    /** Storage for the active transition timeout */
    private transitionTimeout: any | null = null;

    /** Cancel active transition timeout */
    private cancelTransition = () => {
        if (this.transitionTimeout !== null) {
            window.clearTimeout(this.transitionTimeout);
            this.transitionTimeout = null;
        }
    };

    /** Emitted when the sidebar is closed  */
    @Event({ eventName: "uicClose", bubbles: true, cancelable: true })
    sidebarClosed: EventEmitter<string>;

    /** Emitted when the sidebar is opened  */
    @Event({ eventName: "uicOpen", bubbles: true, cancelable: true })
    sidebarOpened: EventEmitter<string>;

    /** Handle opened change */
    @Watch("opened")
    setOpened() {
        const { opened } = this;
        this.cancelTransition();
        if (opened) {
            if (this.status === "opened") {
                return;
            }
            this.sidebarOpened.emit(this.name);
            this.status = "opening";
            this.transitionTimeout = window.setTimeout(() => {
                this.status = "opened";
            }, 50);
        } else {
            if (this.status === "closed") {
                return;
            }
            this.sidebarClosed.emit(this.name);
            this.status = "closing";
            this.transitionTimeout = window.setTimeout(() => {
                this.status = "closed";
            }, 250);
        }
    }

    componentWillLoad() {
        this.setOpened();
    }

    disconnectedCallback() {
        this.cancelTransition();
    }

    render() {
        const { side, status, alignHeader, alignFooter } = this;
        const baseClass = "uic-sidebar";
        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--side-${side}`]: true,
                    [`${baseClass}--align-header`]: alignHeader,
                    [`${baseClass}--align-footer`]: alignFooter,
                    [`${baseClass}--${status}`]: true,
                }}
            >
                <slot />
            </Host>
        );
    }
}
